import React from 'react';
import axios from 'axios';
import { Stethoscope, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import QueuePriorityBadge from '@/components/QueuePriorityBadge';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function WalkinQueuePanel({
  queue, selectedSession, setQueue, onTriage,
}) {
  return (
    <aside className="rounded-2xl border border-[#D5DEE8] bg-white p-4 shadow-sm lg:sticky lg:top-4"
      data-testid="walkin-queue-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base text-[#071B34]">Session queue</h3>
        <span className="text-xs text-[#5A6B7C]">{queue.length} patient{queue.length === 1 ? '' : 's'}</span>
      </div>
      <p className="text-[11px] text-[#5A6B7C] mb-3 leading-snug">
        Queue numbers appear after triage. Appointments are listed before walk-ins.
      </p>
      <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">
        {queue.length === 0 ? (
          <p className="text-sm text-[#5A6B7C]">No check-ins yet for this session.</p>
        ) : queue.map((q) => (
          <div key={q.id} className="flex items-center gap-2 border border-[#D5DEE8] rounded-xl px-3 py-2 hover:bg-[#F8FAFC] transition group"
            data-testid={`rec-queue-${q.id}`}>
            <div className="h-8 w-8 rounded-full bg-[#071B34] text-white text-sm font-bold flex items-center justify-center">
              {q.queue_no || '·'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#071B34] truncate flex items-center gap-1.5 flex-wrap">
                {q.patient_name}
                <QueuePriorityBadge entry={q} />
              </div>
              <div className="text-[10px] text-[#5A6B7C]">
                {q.booking_mode}{q.slot_start ? ` · ${q.slot_start}` : ''}
                {q.payment_status === 'paid' ? ` · paid ₹${q.payment_amount}` : ''}
                {q.payment_status === 'waived' ? ' · waived' : ''}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] opacity-0 group-hover:opacity-100"
              onClick={() => onTriage(q)} data-testid={`rec-triage-${q.id}`}>
              <Stethoscope className="h-3 w-3 mr-1" /> Triage
            </Button>
            {q.status !== 'completed' && q.status !== 'in_consult' && (
              <Button size="sm" variant="ghost"
                className="h-7 px-1.5 text-[10px] opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50"
                onClick={async () => {
                  const label = `${q.patient_name} (Queue #${q.queue_no || '—'})`;
                  if (!window.confirm(`Cancel this queue entry?\n\n${label}`)) return;
                  try {
                    await ax.delete(`/api/clinic-ops/queue/${q.id}`);
                    toast.success('Token cancelled');
                    if (selectedSession) {
                      const r = await ax.get(`/api/clinic-ops/queue?session_id=${selectedSession.id}`);
                      setQueue(r.data || []);
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not cancel');
                  }
                }}
                data-testid={`rec-cancel-${q.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}
